import { NotebookPen } from 'lucide-react'
import RichTextContent from 'app/frontend/base/components/RichTextContent'
import NotesEditor, {
  hasNotesContent,
} from 'app/frontend/base/components/NotesEditor'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import { cn } from '@/lib/utils'

type ControlProps = {
  description: string | undefined
  canEdit: boolean
  isEditing: boolean
  onStartEdit: () => void
  className?: string
}

export const AssignmentDescriptionControl = ({
  description,
  canEdit,
  isEditing,
  onStartEdit,
  className,
}: ControlProps) => {
  const { owner } = useTeamPlanTimeline()
  if (owner.ownerType !== 'team') return null
  if (!canEdit || isEditing) return null

  return (
    <button
      type='button'
      onClick={onStartEdit}
      className={cn(
        'group flex items-center gap-2 self-start rounded-md border border-transparent px-2 py-1 text-sm text-muted-foreground transition-colors hover:border-border hover:bg-accent',
        className,
      )}
      aria-label={
        hasNotesContent(description) ? 'Edit description' : 'Add description'
      }
    >
      <NotebookPen className='h-3.5 w-3.5 opacity-60 transition-opacity group-hover:opacity-100' />
      <span>
        {hasNotesContent(description) ? 'Edit description' : 'Add description'}
      </span>
    </button>
  )
}

type ContentProps = {
  description: string | undefined
  isEditing: boolean
  clearTarget?: string
  onStopEdit: () => void
  onSave: (html: string) => Promise<void>
  className?: string
}

const AssignmentDescriptionContent = ({
  description,
  isEditing,
  clearTarget = 'this assignment',
  onStopEdit,
  onSave,
  className,
}: ContentProps) => {
  const { owner } = useTeamPlanTimeline()
  if (owner.ownerType !== 'team') return null

  if (isEditing) {
    return (
      <NotesEditor
        initialValue={description ?? ''}
        onSave={onSave}
        onCancel={onStopEdit}
        clearTarget={clearTarget}
        className='mb-2'
      />
    )
  }

  if (!hasNotesContent(description)) return null

  return (
    <div className={cn('', className)}>
      <RichTextContent content={description ?? ''} />
    </div>
  )
}

export default AssignmentDescriptionContent
